import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "toto.session_token";
const IMPERSONATE_FROM_EMAIL_KEY = "toto.impersonating_from_email";
const IMPERSONATE_FROM_NAME_KEY = "toto.impersonating_from_name";

export const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setStoredToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
};

export const getImpersonatingFrom = () => {
  try {
    const email = localStorage.getItem(IMPERSONATE_FROM_EMAIL_KEY);
    if (!email) return null;
    return { email, name: localStorage.getItem(IMPERSONATE_FROM_NAME_KEY) || email };
  } catch { return null; }
};
export const setImpersonatingFrom = (u) => {
  try {
    if (u?.email) {
      localStorage.setItem(IMPERSONATE_FROM_EMAIL_KEY, u.email);
      localStorage.setItem(IMPERSONATE_FROM_NAME_KEY, u.name || u.email);
    } else {
      localStorage.removeItem(IMPERSONATE_FROM_EMAIL_KEY);
      localStorage.removeItem(IMPERSONATE_FROM_NAME_KEY);
    }
  } catch {}
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer token on every request as a fallback for browsers/environments
// where cross-site cookies are blocked (Safari ITP, privacy-hardened Chrome, VPN proxies).
api.interceptors.request.use((config) => {
  const t = getStoredToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// ============================================================
// Boutique registry — single source of truth (generated).
//
// Boutique identity ORIGINATES in `/app/backend/boutiques.py`. This module
// re-exports the generated mirror produced by
//   `python -m scripts.sync_boutique_registry_to_frontend`
// and layers the legacy CSS-helper fields (`tint / dot / accent / label`)
// on top so existing consumers keep working. There is NO hand-maintained
// boutique list on the frontend — do not add or rename boutiques here.
// The frontend also verifies at runtime that `/api/boutiques` returns the
// same records; a drift warning surfaces in the console if they diverge.
// ============================================================
import { BOUTIQUE_RECORDS, MARKET_ORDER as GENERATED_MARKET_ORDER } from "@/generated/boutique_registry.generated";

function _legacyHelpers(code) {
  return {
    tint:   `tint-${code}`,
    dot:    `dot-${code}`,
    accent: `left-accent-${code}`,
    label:  code,
    short:  undefined,   // filled from shortName below
  };
}

function _asMeta(rec) {
  return {
    ...rec,
    ..._legacyHelpers(rec.code),
    // `short` was the legacy name for shortName — keep it available for
    // components that haven't migrated yet.
    short: rec.shortName,
  };
}

// Rainbow is delivered as the last record from the backend; split it out
// so `boutiqueMeta` remains a pure {code -> meta} map for real boutiques.
const _rainbowRec = BOUTIQUE_RECORDS.find((r) => r.code === "RBW") || null;
const _boutiqueRecs = BOUTIQUE_RECORDS.filter((r) => r.code !== "RBW");

export const boutiqueMeta = Object.fromEntries(
  _boutiqueRecs.map((r) => [r.code, _asMeta(r)])
);

export const rainbowMeta = _rainbowRec
  ? { ..._asMeta(_rainbowRec), tint: "tint-RNB", dot: "dot-RNB", accent: "left-accent-RNB", label: "RNB" }
  : {
      code: "RBW", name: "Rainbow Interests", shortName: "Rainbow", short: "Rainbow",
      market: "Corporate", state: null, colorFamily: "pearl", presenceTint: "rainbow",
      tint: "tint-RNB", dot: "dot-RNB", accent: "left-accent-RNB", label: "RNB",
    };

export function boutiqueOf(code) {
  return boutiqueMeta[code] || rainbowMeta;
}

/** Ordered list of active boutiques (matches backend canonical ordering). */
export function boutiqueList() {
  return _boutiqueRecs.map((r) => boutiqueMeta[r.code]);
}

export const MARKET_ORDER = GENERATED_MARKET_ORDER;

export function groupBoutiquesByMarket({ includeRainbow = false } = {}) {
  const groups = new Map();
  MARKET_ORDER.forEach((m) => groups.set(m, []));
  boutiqueList().forEach((b) => {
    if (!groups.has(b.market)) groups.set(b.market, []);
    groups.get(b.market).push(b);
  });
  if (includeRainbow) {
    if (!groups.has(rainbowMeta.market)) groups.set(rainbowMeta.market, []);
    groups.get(rainbowMeta.market).push(rainbowMeta);
  }
  return Array.from(groups.entries())
    .filter(([, list]) => list.length > 0)
    .map(([market, list]) => ({ market, boutiques: list }));
}

export const roleLabel = {
  owner: "Owner",
  district_leader: "District Leader",
  boutique_manager: "Boutique Manager",
  front_desk: "Front Desk",
  massage_therapist: "Massage Therapist",
  recruiter: "Recruiter",
  onboarding_admin: "Onboarding Admin",
  trainer: "Trainer",
};
